"use client";

import React, { useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { usePathname, useRouter } from "next/navigation";
import { Lock } from "lucide-react";
import { 
    getStoredToken, 
    setStoredToken, 
    removeStoredToken, 
    userAtom, 
    isLoggingOutAtom, 
    authStatusAtom 
} from "@/store/useStore";
import { auth } from "@/lib/firebase";
import { onIdTokenChanged } from "firebase/auth";
import { useAtom } from "jotai";
import { useLayoutEffect } from "react";
import { toast } from "react-hot-toast";

export default function MainLayout({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const router = useRouter();
    const [user, setUser] = useAtom(userAtom);
    const [isLoggingOut, setIsLoggingOut] = useAtom(isLoggingOutAtom);
    const [status, setStatus] = useAtom(authStatusAtom);
    const syncStarted = React.useRef(false);

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            const { auth: firebaseAuth } = await import("@/lib/firebase");
            await firebaseAuth.signOut();
            removeStoredToken();
            setUser(null);
            setStatus('unauthenticated');
            router.push("/login");
        } catch (error) {
            console.error("Logout error:", error);
            removeStoredToken();
            setUser(null);
            setStatus('unauthenticated');
            router.push("/login");
            setIsLoggingOut(false);
        }
    };

    // Synchronous check before first paint
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;
        
        const token = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');

        if (token) {
            if (storedUser) {
                try { setUser(JSON.parse(storedUser)); } catch (e) {}
            }
            setStatus('authenticated');
        } else {
            setStatus('unauthenticated');
        }
    }, [setStatus, setUser]);

    useEffect(() => {
        // Essential session initialization logic is now in useLayoutEffect for faster render
        
        // Keep status in sync with actual Firebase state changes
        const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const token = await firebaseUser.getIdToken();
                setStoredToken(token);
                setStatus('authenticated');
                
                // If we already have a role in memory, don't re-sync from scratch immediately
                if (user?.role || syncStarted.current) return;
                
                // If we don't have a role, sync with backend immediately
                const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
                if (!currentUser?.role && !syncStarted.current) {
                    syncStarted.current = true;
                    try {
                        const { syncUserWithBackend } = await import('@/store/useStore');
                        const backendUser = await syncUserWithBackend(token);
                        const userData = {
                            displayName: firebaseUser.displayName || "",
                            email: firebaseUser.email || "",
                            photoURL: firebaseUser.photoURL || undefined,
                            role: backendUser?.role?.name,
                        };
                        setUser(userData);
                        localStorage.setItem("user", JSON.stringify(userData));
                        toast.success(`Welcome, ${firebaseUser.displayName}`);
                    } catch (e) {
                        console.error('Role sync failed:', e);
                        syncStarted.current = false;
                    }
                }
            } else {
                if (!getStoredToken()) {
                    setStatus('unauthenticated');
                    removeStoredToken();
                    setUser(null);
                    syncStarted.current = false;
                }
            }
        });

        return () => unsubscribe();
    }, [setUser, setStatus, user?.role]);

    // CENTRALIZED REDIRECTS
    useEffect(() => {
        if (isLoggingOut) return;

        if (status === 'unauthenticated' && pathname !== "/login") {
            router.replace("/login");
        } else if (status === 'authenticated' && pathname === "/login") {
            router.replace("/");
        }
    }, [status, pathname, isLoggingOut, router]);

    // RESET LOGOUT STATE WHEN ARRIVING AT LOGIN
    useEffect(() => {
        if (pathname === "/login" && isLoggingOut) {
            setIsLoggingOut(false);
            setStatus('unauthenticated');
        }
    }, [pathname, isLoggingOut, setIsLoggingOut, setStatus]);

    const FullScreenLoader = () => (
        <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
            <div className="w-10 h-10 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    // BLOCK RENDERING: Prevent any flicker between transitions
    if (isLoggingOut) return <FullScreenLoader />;
    if (status === 'unauthenticated' && pathname !== "/login") return <FullScreenLoader />;
    if (status === 'authenticated' && pathname === "/login") return <FullScreenLoader />;
    if (status === 'checking') return <FullScreenLoader />;

    // ROLE-SYNC CHECK: If authenticated but role is still missing, we are still "initializing"
    const role = user?.role?.toLowerCase();
    const isAuthorized = role === 'admin';

    if (status === 'authenticated' && !role && pathname !== "/login") {
        return <FullScreenLoader />;
    }

    // SPECIAL HANDLING FOR PUBLIC ROUTES (only Login for now)
    if (pathname === "/login") return <>{children}</>;

    // Handle Unauthorized Users within the Layout
    if (status === 'authenticated' && !isAuthorized) {
        return (
            <div className="flex flex-col h-screen overflow-hidden bg-slate-50 font-sans">
                <Header />
                <div className="flex flex-1 overflow-hidden items-center justify-center p-6">
                    <main className="max-w-md w-full text-center space-y-6">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto text-slate-400">
                            <Lock size={32} />
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-xl font-bold text-slate-900">Admin Access Required</h2>
                            <p className="text-sm text-slate-500 font-medium">
                                This portal is restricted to **administrators**.
                            </p>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    // Default Authorized Layout
    return (
        <div className="flex flex-col h-screen overflow-hidden bg-slate-50 font-sans">
            <Header />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 overflow-y-auto p-6">{children}</main>
            </div>
        </div>
    );
}
